import React from 'react';
import CharacterImportReviewModal from './features/character-import';
import { isCampaignTestCharacter } from './campaignCharacterTargets';

const palette = {
  bg: '#15100b',
  panel: 'rgba(26, 20, 14, 0.92)',
  panelAlt: 'rgba(37, 29, 19, 0.86)',
  border: 'rgba(201, 162, 39, 0.35)',
  accent: '#c9a227',
  text: '#e8dcc4',
  textDim: '#a89778',
  danger: '#d46a5a',
  success: '#6fa36f',
};

const normalizeCode = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const formatCode = (value) => {
  const normalized = normalizeCode(value).slice(0, 12);
  return normalized.replace(/(.{4})(?=.)/g, '$1-');
};

const styles = {
  shell: {
    minHeight: '100vh',
    background: palette.bg,
    color: palette.text,
    padding: '32px 18px',
    fontFamily: 'serif',
  },
  wrap: {
    maxWidth: 1120,
    margin: '0 auto',
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    color: palette.accent,
    fontSize: '1.8rem',
    letterSpacing: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 340px) minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
  },
  panel: {
    background: palette.panel,
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    padding: 16,
    boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
  },
  panelTitle: {
    margin: '0 0 12px',
    color: palette.accent,
    fontSize: '1.1rem',
    letterSpacing: 0,
  },
  sectionTitle: {
    margin: '0 0 10px',
    color: palette.accent,
    fontSize: '1rem',
    letterSpacing: 0,
  },
  inviteBlock: {
    borderTop: '1px solid rgba(232,220,196,0.14)',
    paddingTop: 14,
    marginTop: 2,
  },
  muted: {
    color: palette.textDim,
    fontSize: '0.9rem',
    lineHeight: 1.4,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  row: {
    width: '100%',
    textAlign: 'left',
    background: palette.panelAlt,
    color: palette.text,
    border: '1px solid rgba(232,220,196,0.14)',
    borderRadius: 6,
    padding: 12,
  },
  rowButton: {
    cursor: 'pointer',
  },
  activeRow: {
    border: `1px solid ${palette.accent}`,
    background: 'rgba(201,162,39,0.12)',
  },
  meta: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 6,
    color: palette.textDim,
    fontSize: '0.78rem',
  },
  formRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  input: {
    flex: '1 1 180px',
    minWidth: 0,
    background: 'rgba(0,0,0,0.34)',
    border: '1px solid rgba(232,220,196,0.22)',
    color: palette.text,
    borderRadius: 4,
    padding: '10px 11px',
    fontSize: '0.95rem',
  },
  button: {
    background: palette.accent,
    color: '#21170b',
    border: 'none',
    borderRadius: 4,
    padding: '10px 12px',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  secondaryButton: {
    background: 'rgba(232,220,196,0.08)',
    color: palette.text,
    border: '1px solid rgba(232,220,196,0.24)',
  },
  dangerButton: {
    background: 'rgba(212,106,90,0.18)',
    color: palette.danger,
    border: '1px solid rgba(212,106,90,0.42)',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  split: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 12,
  },
  notice: {
    border: '1px solid rgba(111,163,111,0.45)',
    background: 'rgba(111,163,111,0.12)',
    color: palette.text,
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
  },
  error: {
    border: '1px solid rgba(212,106,90,0.45)',
    background: 'rgba(212,106,90,0.12)',
    color: palette.text,
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
  },
  pre: {
    margin: '10px 0 0',
    maxHeight: 260,
    overflow: 'auto',
    background: 'rgba(0,0,0,0.34)',
    border: '1px solid rgba(232,220,196,0.16)',
    borderRadius: 6,
    padding: 10,
    color: palette.text,
    fontSize: '0.76rem',
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
  },
};

const roleLabel = (role) => {
  if (role === 'owner') return 'Owner';
  if (role === 'dm') return 'DM';
  return 'Player';
};

export const DEMO_CAMPAIGN_CHARACTERS = [
  { charId: 'demo-lika', name: "Li'ka" },
  { charId: 'demo-ventyl', name: 'Ventyl' },
  { charId: 'demo-eve', name: 'Eve' },
  { charId: 'demo-khan', name: 'Khan' },
];

const CharacterSummary = ({ character }) => (
  <div>
    <strong>{character.name || 'Unnamed Character'}</strong>
    <div style={styles.meta}>
      <span>{character.race || 'Unknown race'}</span>
      <span>{character.charClass || 'Adventurer'}</span>
      <span>Level {character.level || 1}</span>
      {character.maxHp !== null && character.maxHp !== undefined && (
        <span>HP {character.currentHp ?? '?'} / {character.maxHp}</span>
      )}
    </div>
  </div>
);

const isSavedPracticeCharacter = (character) => (
  isCampaignTestCharacter(character)
);

export const CampaignLandingView = ({
  campaignsData,
  standaloneCharacters,
  selectedCampaignId,
  rosterData,
  inviteData,
  preview,
  createdInvite,
  inviteMaxUses = '6',
  joinCode,
  newCharacterName = '',
  newCharacterRace = '',
  newCharacterClass = '',
  newCharacterMaxHp = '',
  busy,
  error,
  notice,
  recoveryLookup,
  recoveryPassword,
  devAccessData,
  supportCharacter,
  onSelectCampaign,
  onNewCharacterNameChange,
  onNewCharacterRaceChange,
  onNewCharacterClassChange,
  onNewCharacterMaxHpChange,
  onCreateCampaignCharacter,
  onInviteMaxUsesChange,
  onCreateInvite,
  onCopyInviteCode,
  onRevokeInvite,
  onJoinCodeChange,
  onPreviewInvite,
  onAcceptInvite,
  onRecoveryLookupChange,
  onRecoveryPasswordChange,
  onRecoverStandaloneCharacter,
  onOpenSupportCharacter,
  onEnterCampaignCharacter,
  onEnterCampaignDm,
  onEnterDemoCampaign,
  onAddCharacterToCampaign,
  onOpenCampaignCharacterImport,
  onEnterStandalone,
  onSignOut,
}) => {
  const campaigns = React.useMemo(() => campaignsData?.campaigns || [], [campaignsData?.campaigns]);
  const selectedCampaign = campaigns.find((campaign) => campaign.campaignId === selectedCampaignId) || null;
  const liveRoster = React.useMemo(
    () => (rosterData?.characters || []).filter((character) => !isSavedPracticeCharacter(character)),
    [rosterData?.characters],
  );
  const invites = inviteData?.invites || [];
  const standalone = standaloneCharacters || [];
  const devCharacters = devAccessData?.characters || [];
  const loading = campaignsData === undefined || standaloneCharacters === undefined;
  const canManageCampaign = selectedCampaign?.role === 'owner' || selectedCampaign?.role === 'dm';
  const visibleCreatedInvite = createdInvite?.campaignId === selectedCampaign?.campaignId
    ? createdInvite
    : null;

  return (
    <div style={styles.shell}>
      <div style={styles.wrap}>
        <div style={styles.top}>
          <div>
            <h1 style={styles.title}>Survival Tracker</h1>
            <div style={styles.muted}>Choose a campaign or standalone character.</div>
          </div>
          <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={onSignOut}>
            Sign out
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {notice && <div style={styles.notice}>{notice}</div>}

        <div style={styles.grid}>
          <div style={styles.stack}>
            <section style={styles.panel}>
              <h2 style={styles.panelTitle}>My Campaigns</h2>
              {loading ? (
                <div style={styles.muted}>Loading campaigns...</div>
              ) : campaigns.length === 0 ? (
                <div style={styles.muted}>No campaigns yet.</div>
              ) : (
                <div style={styles.list}>
                  {campaigns.map((campaign) => (
                    <button
                      key={campaign.campaignId}
                      type="button"
                      style={{
                        ...styles.row,
                        ...styles.rowButton,
                        ...(campaign.campaignId === selectedCampaignId ? styles.activeRow : {}),
                      }}
                      onClick={() => onSelectCampaign(campaign.campaignId)}
                    >
                      <strong>{campaign.name}</strong>
                      <div style={styles.meta}>
                        <span>{roleLabel(campaign.role)}</span>
                        {campaign.isActive && <span>Active</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section style={styles.panel}>
              <h2 style={styles.panelTitle}>Demo Campaign</h2>
              <div style={{ ...styles.row, marginBottom: 10 }}>
                <strong>Original Demo Party</strong>
                <div style={styles.meta}>
                  {DEMO_CAMPAIGN_CHARACTERS.map((character) => (
                    <span key={character.charId}>{character.name}</span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={() => onEnterDemoCampaign({ role: 'dm' })}
                >
                  Open Demo DM
                </button>
                {DEMO_CAMPAIGN_CHARACTERS.map((character) => (
                  <button
                    key={character.charId}
                    type="button"
                    style={styles.button}
                    onClick={() => onEnterDemoCampaign({ role: 'player', charId: character.charId })}
                  >
                    Play {character.name}
                  </button>
                ))}
              </div>
            </section>

            <section style={styles.panel}>
              <h2 style={styles.panelTitle}>Join With Code</h2>
              <div style={styles.formRow}>
                <input
                  style={styles.input}
                  value={joinCode}
                  onChange={(event) => onJoinCodeChange(formatCode(event.target.value))}
                  placeholder="ABCD-EFGH-JKLM"
                />
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={onPreviewInvite}>
                  Preview
                </button>
              </div>
              {preview && (
                <div style={{ ...styles.row, marginTop: 10 }}>
                  {preview.valid ? (
                    <>
                      <strong>{preview.campaignName}</strong>
                      <div style={styles.meta}>
                        <span>{roleLabel(preview.roleGrant)}</span>
                        {preview.maxUses !== null && <span>{preview.useCount} / {preview.maxUses} used</span>}
                      </div>
                    </>
                  ) : (
                    <span style={{ color: palette.danger }}>Invite unavailable: {preview.reason}</span>
                  )}
                </div>
              )}
              <button
                type="button"
                style={{ ...styles.button, width: '100%', marginTop: 10 }}
                disabled={busy === 'acceptInvite'}
                onClick={onAcceptInvite}
              >
                Join Campaign
              </button>
            </section>
          </div>

          <div style={styles.stack}>
            <section style={styles.panel}>
              <h2 style={styles.panelTitle}>
                {selectedCampaign ? selectedCampaign.name : 'Campaign Roster'}
              </h2>
              {!selectedCampaign ? (
                <div style={styles.muted}>Select or create a campaign to view its roster.</div>
              ) : rosterData === undefined ? (
                <div style={styles.muted}>Loading roster...</div>
              ) : (
                <div style={styles.stack}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div style={styles.muted}>{roleLabel(selectedCampaign.role)} access</div>
                    {canManageCampaign && (
                      <button
                        type="button"
                        style={{ ...styles.button, ...styles.secondaryButton }}
                        onClick={() => onEnterCampaignDm(selectedCampaign.campaignId)}
                      >
                        Open DM View
                      </button>
                    )}
                  </div>
                  {liveRoster.length === 0 ? (
                    <div style={styles.muted}>No campaign characters yet.</div>
                  ) : (
                    <div style={styles.list}>
                      {liveRoster.map((character) => (
                        <div key={character.charId} style={styles.row}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                            <CharacterSummary character={character} />
                            {character.isPlayable && (
                              <button
                                type="button"
                                style={styles.button}
                                onClick={() => onEnterCampaignCharacter(selectedCampaign.campaignId, character.charId)}
                              >
                                Enter
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {canManageCampaign && (
                    <div style={styles.inviteBlock}>
                      <h3 style={styles.sectionTitle}>Campaign Invites</h3>
                      <div style={{ ...styles.formRow, flexWrap: 'wrap' }}>
                        <input
                          style={{ ...styles.input, flex: '0 1 120px' }}
                          type="number"
                          min="1"
                          max="100"
                          value={inviteMaxUses}
                          onChange={(event) => onInviteMaxUsesChange(event.target.value)}
                          placeholder="Uses"
                          aria-label="Invite max uses"
                        />
                        <button
                          type="button"
                          style={styles.button}
                          disabled={busy === `createInvite:${selectedCampaign.campaignId}`}
                          onClick={() => onCreateInvite(selectedCampaign.campaignId)}
                        >
                          Create Invite
                        </button>
                      </div>
                      {visibleCreatedInvite?.code && (
                        <div style={{ ...styles.row, marginTop: 10 }}>
                          <strong>{visibleCreatedInvite.code}</strong>
                          <div style={styles.meta}>
                            <span>{visibleCreatedInvite.maxUses} uses</span>
                            <span>Active</span>
                          </div>
                          <button
                            type="button"
                            style={{ ...styles.button, ...styles.secondaryButton, marginTop: 10 }}
                            onClick={() => onCopyInviteCode(visibleCreatedInvite.code)}
                          >
                            Copy
                          </button>
                        </div>
                      )}
                      {inviteData === undefined ? (
                        <div style={{ ...styles.muted, marginTop: 10 }}>Loading invites...</div>
                      ) : invites.length === 0 ? (
                        <div style={{ ...styles.muted, marginTop: 10 }}>No active invite history.</div>
                      ) : (
                        <div style={{ ...styles.list, marginTop: 10 }}>
                          {invites.map((invite) => (
                            <div key={invite.inviteId} style={styles.row}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                                <div>
                                  <strong>{invite.status === 'revoked' ? 'Revoked invite' : 'Active invite'}</strong>
                                  <div style={styles.meta}>
                                    <span>{roleLabel(invite.roleGrant)}</span>
                                    {invite.maxUses !== null && <span>{invite.useCount} / {invite.maxUses} used</span>}
                                    {invite.isExpired && <span>Expired</span>}
                                    {invite.isUsedUp && <span>Used up</span>}
                                  </div>
                                </div>
                                {invite.status === 'active' && (
                                  <button
                                    type="button"
                                    style={{ ...styles.button, ...styles.dangerButton }}
                                    disabled={busy === `revokeInvite:${invite.inviteId}`}
                                    onClick={() => onRevokeInvite(selectedCampaign.campaignId, invite.inviteId)}
                                  >
                                    Revoke
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            <section style={styles.split}>
              <div style={styles.panel}>
                <h2 style={styles.panelTitle}>Create Campaign Character</h2>
                {!selectedCampaign ? (
                  <div style={styles.muted}>Select a campaign first.</div>
                ) : (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      onCreateCampaignCharacter(selectedCampaign.campaignId);
                    }}
                  >
                    <div style={{ ...styles.formRow, flexWrap: 'wrap' }}>
                      <input
                        style={styles.input}
                        value={newCharacterName}
                        onChange={(event) => onNewCharacterNameChange(event.target.value)}
                        placeholder="Name"
                      />
                      <input
                        style={styles.input}
                        value={newCharacterRace}
                        onChange={(event) => onNewCharacterRaceChange(event.target.value)}
                        placeholder="Race"
                      />
                    </div>
                    <div style={{ ...styles.formRow, flexWrap: 'wrap' }}>
                      <input
                        style={styles.input}
                        value={newCharacterClass}
                        onChange={(event) => onNewCharacterClassChange(event.target.value)}
                        placeholder="Class"
                      />
                      <input
                        style={styles.input}
                        type="number"
                        min="1"
                        max="999"
                        value={newCharacterMaxHp}
                        onChange={(event) => onNewCharacterMaxHpChange(event.target.value)}
                        placeholder="Max HP"
                      />
                    </div>
                    <button
                      type="submit"
                      style={{ ...styles.button, width: '100%', marginTop: 10 }}
                      disabled={busy === 'createCampaignCharacter'}
                    >
                      Create
                    </button>
                  </form>
                )}
              </div>

              <div style={styles.panel}>
                <h2 style={styles.panelTitle}>Add Character to Campaign</h2>
                {!selectedCampaign ? (
                  <div style={styles.muted}>Select a campaign first.</div>
                ) : (
                  <div style={styles.stack}>
                    <button
                      type="button"
                      style={{ ...styles.button, width: '100%' }}
                      onClick={() => onOpenCampaignCharacterImport(selectedCampaign.campaignId)}
                    >
                      Import Character Sheet
                    </button>
                    <h3 style={styles.sectionTitle}>Choose Solo Character</h3>
                    {standalone.length === 0 ? (
                      <div style={styles.muted}>No standalone characters are available to add.</div>
                    ) : (
                      <div style={styles.list}>
                        {standalone.map((character) => (
                          <div key={character.charId} style={styles.row}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                              <CharacterSummary character={character} />
                              <button
                                type="button"
                                style={{ ...styles.button, ...styles.secondaryButton }}
                                disabled={busy === `clone:${character.charId}`}
                                onClick={() => onAddCharacterToCampaign(selectedCampaign.campaignId, character.charId)}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={styles.panel}>
                <h2 style={styles.panelTitle}>My Standalone Characters</h2>
                {loading ? (
                  <div style={styles.muted}>Loading characters...</div>
                ) : standalone.length === 0 ? (
                  <div style={styles.muted}>No standalone characters yet.</div>
                ) : (
                  <div style={styles.list}>
                    {standalone.map((character) => (
                      <div key={character.charId} style={styles.row}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                          <CharacterSummary character={character} />
                          <button
                            type="button"
                            style={styles.button}
                            disabled={busy === `standalone:${character.charId}`}
                            onClick={() => onEnterStandalone(character.charId)}
                          >
                            Enter
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section style={styles.panel}>
              <h2 style={styles.panelTitle}>Recover Existing Character</h2>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  onRecoverStandaloneCharacter();
                }}
              >
                <div style={styles.formRow}>
                  <input
                    style={styles.input}
                    value={recoveryLookup}
                    onChange={(event) => onRecoveryLookupChange(event.target.value)}
                    placeholder="Character name or ID"
                  />
                  <input
                    style={styles.input}
                    type="password"
                    value={recoveryPassword}
                    onChange={(event) => onRecoveryPasswordChange(event.target.value)}
                    placeholder="Character password"
                  />
                  <button type="submit" style={styles.button} disabled={busy === 'recoverStandalone'}>
                    Recover
                  </button>
                </div>
              </form>
            </section>

            {devAccessData?.hasAccess && (
              <section style={styles.panel}>
                <h2 style={styles.panelTitle}>Beta Character Access</h2>
                <div style={styles.meta}>
                  <span>{devAccessData.role}</span>
                  <span>{devCharacters.length} standalone</span>
                </div>
                {devCharacters.length === 0 ? (
                  <div style={{ ...styles.muted, marginTop: 10 }}>No standalone characters found.</div>
                ) : (
                  <div style={{ ...styles.list, marginTop: 10 }}>
                    {devCharacters.map((character) => (
                      <div key={character.charId} style={styles.row}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                          <div>
                            <CharacterSummary character={character} />
                            <div style={styles.meta}>
                              <span>{character.ownerEmail || 'Unowned'}</span>
                              {character.hasPassword && <span>Legacy password</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            style={{ ...styles.button, ...styles.secondaryButton }}
                            disabled={busy === `support:${character.charId}`}
                            onClick={() => onOpenSupportCharacter(character.charId)}
                          >
                            Inspect
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {supportCharacter && (
                  <div style={{ ...styles.row, marginTop: 12 }}>
                    <strong>{supportCharacter.name || supportCharacter.charId}</strong>
                    <div style={styles.meta}>
                      <span>{supportCharacter.ownerEmail || 'Unowned'}</span>
                      <span>{supportCharacter.charId}</span>
                    </div>
                    <pre style={styles.pre}>{JSON.stringify(supportCharacter.data || {}, null, 2)}</pre>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const describeError = (error) => error?.message || String(error || 'Something went wrong');

export default function CampaignLanding({
  useQuery,
  useMutation,
  api,
  onEnterApp,
  onSignOut,
  initialSelectedCampaignId = null,
  characterImportUploadClient = null,
}) {
  const campaignsData = useQuery(api.campaigns.listMine, {});
  const standaloneCharacters = useQuery(api.campaigns.listMyStandaloneCharacters, {});
  const devAccessData = useQuery(api.devAccess.listStandaloneCharacterSummaries, {});
  const [selectedCampaignId, setSelectedCampaignId] = React.useState(initialSelectedCampaignId);
  const appliedInitialCampaignRef = React.useRef(null);
  const [joinCode, setJoinCode] = React.useState('');
  const [newCharacterName, setNewCharacterName] = React.useState('');
  const [newCharacterRace, setNewCharacterRace] = React.useState('');
  const [newCharacterClass, setNewCharacterClass] = React.useState('');
  const [newCharacterMaxHp, setNewCharacterMaxHp] = React.useState('');
  const [inviteMaxUses, setInviteMaxUses] = React.useState('6');
  const [createdInvite, setCreatedInvite] = React.useState(null);
  const [recoveryLookup, setRecoveryLookup] = React.useState('');
  const [recoveryPassword, setRecoveryPassword] = React.useState('');
  const [supportCharacter, setSupportCharacter] = React.useState(null);
  const [importCampaignId, setImportCampaignId] = React.useState(null);
  const [previewCode, setPreviewCode] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  const campaigns = React.useMemo(() => campaignsData?.campaigns || [], [campaignsData?.campaigns]);
  const selectedCampaign = React.useMemo(
    () => campaigns.find((campaign) => campaign.campaignId === selectedCampaignId) || null,
    [campaigns, selectedCampaignId],
  );
  const findCampaignLaunchMetadata = React.useCallback((campaignId) => {
    const campaign = campaigns.find((row) => row.campaignId === campaignId);
    if (!campaign) return {};
    const metadata = {};
    if (Object.prototype.hasOwnProperty.call(campaign, 'enabledFeatures')) {
      metadata.enabledFeatures = campaign.enabledFeatures || null;
    }
    if (Object.prototype.hasOwnProperty.call(campaign, 'homebrew')) {
      metadata.homebrew = campaign.homebrew || null;
    }
    return metadata;
  }, [campaigns]);
  const canManageSelectedCampaign = selectedCampaign?.role === 'owner' || selectedCampaign?.role === 'dm';
  React.useEffect(() => {
    if (
      initialSelectedCampaignId
      && appliedInitialCampaignRef.current !== initialSelectedCampaignId
      && campaigns.some((campaign) => campaign.campaignId === initialSelectedCampaignId)
    ) {
      appliedInitialCampaignRef.current = initialSelectedCampaignId;
      setSelectedCampaignId(initialSelectedCampaignId);
      return;
    }
    if (selectedCampaignId && campaigns.some((campaign) => campaign.campaignId === selectedCampaignId)) return;
    if (campaignsData?.activeCampaignId) {
      setSelectedCampaignId(campaignsData.activeCampaignId);
      return;
    }
    if (campaigns.length > 0) setSelectedCampaignId(campaigns[0].campaignId);
  }, [campaigns, campaignsData?.activeCampaignId, initialSelectedCampaignId, selectedCampaignId]);

  const rosterData = useQuery(
    api.campaigns.listCampaignRoster,
    selectedCampaignId ? { campaignId: selectedCampaignId } : 'skip',
  );
  const findRosterCharacter = React.useCallback((campaignId, charId) => {
    if (!rosterData || rosterData.campaignId !== campaignId) return null;
    return (rosterData.characters || []).find((character) => character.charId === charId) || null;
  }, [rosterData]);
  const inviteData = useQuery(
    api.campaigns.listCampaignInvites,
    selectedCampaignId && canManageSelectedCampaign ? { campaignId: selectedCampaignId } : 'skip',
  );
  const preview = useQuery(
    api.campaigns.previewInvite,
    previewCode ? { code: previewCode } : 'skip',
  );

  const createInvite = useMutation(api.campaigns.createInvite);
  const revokeInvite = useMutation(api.campaigns.revokeInvite);
  const acceptInvite = useMutation(api.campaigns.acceptInvite);
  const setActiveCampaign = useMutation(api.campaigns.setActiveCampaign);
  const setActiveCampaignCharacter = useMutation(api.campaigns.setActiveCampaignCharacter);
  const setActiveStandaloneCharacter = useMutation(api.campaigns.setActiveStandaloneCharacter);
  const recoverStandaloneCharacter = useMutation(api.campaigns.recoverStandaloneCharacter);
  const createCampaignCharacter = useMutation(api.campaigns.createCampaignCharacter);
  const createCampaignCharacterFromImport = useMutation(api.campaigns.createCampaignCharacterFromImport);
  const cloneOwnedCharacterIntoCampaign = useMutation(api.campaigns.cloneOwnedCharacterIntoCampaign);
  const openStandaloneCharacterSupportView = useMutation(api.devAccess.openStandaloneCharacterSupportView);

  const run = async (busyKey, action) => {
    setBusy(busyKey);
    setError(null);
    setNotice(null);
    try {
      return await action();
    } catch (err) {
      setError(describeError(err));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const handleApplyCampaignImportCreate = async ({ createArgs, patch }) => {
    if (!importCampaignId) throw new Error('Select a campaign before importing a character.');
    const importData = {
      ...(patch || {}),
      name: createArgs?.name || patch?.name || 'Imported',
      race: createArgs?.race || patch?.race || 'Unknown',
      maxHp: Number.isFinite(createArgs?.maxHp) ? createArgs.maxHp : patch?.maxHp,
    };
    delete importData.password;
    const result = await createCampaignCharacterFromImport({
      campaignId: importCampaignId,
      data: JSON.stringify(importData),
    });
    setNotice(`Added ${result?.character?.name || importData.name || 'character'} to the campaign.`);
    setImportCampaignId(null);
    return { characterId: result?.charId || null };
  };

  const importCampaign = importCampaignId
    ? campaigns.find((campaign) => campaign.campaignId === importCampaignId) || null
    : null;

  return (
    <>
      <CampaignLandingView
        campaignsData={campaignsData}
        standaloneCharacters={standaloneCharacters}
        selectedCampaignId={selectedCampaignId}
        rosterData={rosterData}
        inviteData={inviteData}
        preview={preview}
        createdInvite={createdInvite}
        inviteMaxUses={inviteMaxUses}
        joinCode={joinCode}
        newCharacterName={newCharacterName}
        newCharacterRace={newCharacterRace}
        newCharacterClass={newCharacterClass}
        newCharacterMaxHp={newCharacterMaxHp}
        busy={busy}
        error={error}
        notice={notice}
        recoveryLookup={recoveryLookup}
        recoveryPassword={recoveryPassword}
        devAccessData={devAccessData}
        supportCharacter={supportCharacter}
        onSignOut={onSignOut}
        onSelectCampaign={(campaignId) => run('selectCampaign', async () => {
          await setActiveCampaign({ campaignId });
          setSelectedCampaignId(campaignId);
        })}
        onJoinCodeChange={(value) => {
          setJoinCode(value);
          setPreviewCode(null);
        }}
        onPreviewInvite={() => {
          setError(null);
          setNotice(null);
          setPreviewCode(joinCode);
        }}
        onAcceptInvite={() => run('acceptInvite', async () => {
          const result = await acceptInvite({ code: joinCode });
          if (!result) return;
          setPreviewCode(null);
          setJoinCode('');
          setSelectedCampaignId(result.campaignId);
          setNotice(result.status === 'already_member'
            ? 'You are already in that campaign.'
            : 'Joined campaign. Add or enter a campaign character from the roster.');
        })}
        onRecoveryLookupChange={setRecoveryLookup}
        onRecoveryPasswordChange={setRecoveryPassword}
        onRecoverStandaloneCharacter={() => run('recoverStandalone', async () => {
          const result = await recoverStandaloneCharacter({
            characterNameOrId: recoveryLookup,
            password: recoveryPassword,
          });
          if (!result) return;
          setRecoveryLookup('');
          setRecoveryPassword('');
          setNotice(`Recovered ${result.name}.`);
        })}
        onOpenSupportCharacter={(charId) => run(`support:${charId}`, async () => {
          const result = await openStandaloneCharacterSupportView({ charId });
          if (!result?.character) return;
          setSupportCharacter(result.character);
          setNotice(`Opened support view for ${result.character.name}.`);
        })}
        onNewCharacterNameChange={setNewCharacterName}
        onNewCharacterRaceChange={setNewCharacterRace}
        onNewCharacterClassChange={setNewCharacterClass}
        onNewCharacterMaxHpChange={setNewCharacterMaxHp}
        onInviteMaxUsesChange={setInviteMaxUses}
        onCreateInvite={(campaignId) => run(`createInvite:${campaignId}`, async () => {
          const maxUses = inviteMaxUses ? Number(inviteMaxUses) : undefined;
          const result = await createInvite({
            campaignId,
            ...(maxUses === undefined ? {} : { maxUses }),
          });
          if (!result) return;
          setCreatedInvite(result);
          setNotice(`Created invite ${result.code}.`);
        })}
        onCopyInviteCode={(code) => run('copyInvite', async () => {
          if (!code) return;
          try {
            if (typeof window !== 'undefined' && window.navigator?.clipboard?.writeText) {
              await window.navigator.clipboard.writeText(code);
              setNotice('Invite code copied.');
            } else {
              setNotice(`Invite code ready: ${code}`);
            }
          } catch (_err) {
            setNotice(`Invite code ready: ${code}`);
          }
        })}
        onRevokeInvite={(campaignId, inviteId) => run(`revokeInvite:${inviteId}`, async () => {
          await revokeInvite({ campaignId, inviteId });
          if (createdInvite?.inviteId === inviteId) setCreatedInvite(null);
          setNotice('Invite revoked.');
        })}
        onCreateCampaignCharacter={(campaignId) => run('createCampaignCharacter', async () => {
          const result = await createCampaignCharacter({
            campaignId,
            name: newCharacterName,
            race: newCharacterRace || undefined,
            charClass: newCharacterClass || undefined,
            maxHp: newCharacterMaxHp ? Number(newCharacterMaxHp) : undefined,
          });
          if (!result) return;
          setNewCharacterName('');
          setNewCharacterRace('');
          setNewCharacterClass('');
          setNewCharacterMaxHp('');
          setNotice(`Created ${result.character?.name || 'campaign character'}.`);
        })}
        onEnterCampaignDm={(campaignId) => run('enterDm', async () => {
          await setActiveCampaign({ campaignId });
          onEnterApp({
            type: 'campaignDm',
            campaignId,
            waitForContext: true,
            ...findCampaignLaunchMetadata(campaignId),
          });
        })}
        onEnterCampaignCharacter={(campaignId, charId) => run(`campaign:${charId}`, async () => {
          const character = findRosterCharacter(campaignId, charId);
          if (isSavedPracticeCharacter(character)) {
            setNotice('Practice characters open only in sandbox.');
            return;
          }
          await setActiveCampaignCharacter({ campaignId, charId });
          onEnterApp({
            type: 'campaignChar',
            campaignId,
            charId,
            waitForContext: true,
            ...findCampaignLaunchMetadata(campaignId),
          });
        })}
        onEnterDemoCampaign={({ role, charId }) => {
          onEnterApp({
            type: 'syntheticDemoCampaign',
            campaignId: 'demo-campaign',
            role,
            charId: role === 'player' ? charId : null,
            waitForContext: false,
            storageMode: 'localDemo',
          });
        }}
        onAddCharacterToCampaign={(campaignId, sourceCharId) => run(`clone:${sourceCharId}`, async () => {
          const result = await cloneOwnedCharacterIntoCampaign({ campaignId, sourceCharId });
          if (!result) return;
          setNotice(result.reusedExisting ? 'That character is already in this campaign.' : 'Character added to the campaign roster.');
        })}
        onOpenCampaignCharacterImport={(campaignId) => {
          setError(null);
          setNotice(null);
          setImportCampaignId(campaignId);
        }}
        onEnterStandalone={(charId) => run(`standalone:${charId}`, async () => {
          await setActiveStandaloneCharacter({ charId });
          onEnterApp({ type: 'standaloneChar', charId, waitForContext: true });
        })}
      />
      {importCampaignId && (
        <CharacterImportReviewModal
          mode="create"
          availableSources={['shenyi', 'vental']}
          uploadAppPayload={characterImportUploadClient}
          onClose={() => setImportCampaignId(null)}
          onApplyCreate={handleApplyCampaignImportCreate}
          title={importCampaign?.name ? `Import Character to ${importCampaign.name}` : 'Import Campaign Character'}
        />
      )}
    </>
  );
}
